import { Anthropic } from '@anthropic-ai/sdk'
import { ColumnDef } from '@tanstack/react-table'
import { Modal, Spin } from 'antd'
import { useRuntimeVariables } from 'app-kit'
import CantileverButton from 'components/CantileverButton'
import CantileverCard from 'components/CantileverCard'
import { Checkbox } from 'components/Checkbox'
import { TextInput } from 'components/form/inputs/TextInput'
import { TagCell } from 'components/table/cells/TagCell'
import { TextCell } from 'components/table/cells/TextCell'
import { parseISO } from 'date-fns'
import dayjs from 'dayjs'
import { AssetFilesTable } from 'features/assetDatabase/components/assetOverview/assetFiles/AssetFilesTable'
import { FilesTableEntry } from 'features/assetDatabase/components/assetOverview/assetFiles/FilesTable'
import { useSiteFilesData } from 'features/assetDatabase/hooks/useSiteFilesData'
import { useGeckoAuth } from 'features/auth'
import {
  DEFAULT_DAMAGE_AREAS_PLAN_KEY,
  getComponentHealthCondition,
  HealthConditionColors,
  isErrorInfo,
  useGetBulkComponentMinRls,
  useGetBulkDamageAreas,
  useRolodexEdit,
} from 'features/fde'
import { PermissionsGate } from 'features/permissions'
import {
  useGetRolodexEntriesByLoadOptions,
  useRolodexFileLS,
  useRolodexSignedURLs,
} from 'features/rolodex'
import { askClaude } from 'features/vertexService'
import { jsPDF } from 'jspdf'
import mammoth from 'mammoth'
import React, { useCallback, useEffect, useRef, useState } from 'react'
import styled from 'styled-components'
import { useGeckoTheme } from 'styles/GeckoTheme'
import {
  GeckoUnit,
  InputType,
  PERMISSION_CANTILEVER_ASSET_OVERVIEW_EDITOR,
  RolodexEntryPutJson,
  RolodexEntryType,
  RolodexFilePath,
} from 'types'

const ContentContainer = styled.div`
  padding: 0.5rem;
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`

const ButtonContainer = styled.div`
  display: flex;
  justify-content: end;
  gap: 0.5rem;
`

const NoBriefContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  height: 2.875rem;
  align-self: stretch;
  color: ${({ theme }) => theme.forecast.tertiaryText};
`

const ModalContentContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`

const SummarySection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
`

const SectionTitle = styled.h4`
  margin: 0;
  font-weight: 600;
  font-size: 1rem;
`

const SectionText = styled.span`
  line-height: 1.5;
`

enum LoadingStage {
  NotLoading,
  BuildingContent,
  Summarizing,
  Saving,
}

interface FileDetails {
  entry_ID: string
  signed_url: string
  file_type: string
}

interface CheckboxCellProps {
  row: FilesTableEntry
  onCheckboxClick: (fileDetails: FileDetails) => void
  checked: boolean
}

interface ComponentNameAndMinRL {
  componentName: string
  remainingLife: number
}

// Move this to a helpers file
const convertDOCXtoPDFBlob = async (file: ArrayBuffer): Promise<Blob> => {
  const mammothFile = await mammoth.convertToHtml({ arrayBuffer: file })
  const mammothHTML = mammothFile.value
  const pdf = new jsPDF()
  return new Promise<Blob>((resolve) =>
    pdf.html(mammothHTML, {
      callback: (doc) => {
        const pdfBlob = doc.output('blob')
        resolve(pdfBlob)
      },
    })
  )
}

// Move to it's own file
const CheckboxCell: React.FC<CheckboxCellProps> = ({
  row,
  onCheckboxClick,
  checked,
}) => {
  const { fileRefs } = useRolodexFileLS({
    entry_id: row.id,
    path: '**',
  })

  const firstRef = fileRefs.at(0)
  const filePath: RolodexFilePath = {
    entry_id: firstRef?.entry_id || '',
    path: firstRef?.path || '',
  }
  const { url } = useRolodexSignedURLs(firstRef ? filePath : undefined)

  if (url) {
    return (
      <Checkbox
        checked={checked}
        onClick={(event) => {
          if (url) {
            onCheckboxClick({
              entry_ID: row.id,
              signed_url: url,
              file_type: row.fileType,
            })
          }
          event.stopPropagation()
        }}
      />
    )
  } else {
    return <></>
  }
}

interface AssetBriefProps {
  unit: GeckoUnit
  refreshOverviewData: () => Promise<void>
  planDetails?: Anthropic.TextBlockParam[]
  regeneratePlanTrigger?: number
  hideGenerateButton?: boolean
}

export const AssetBrief: React.FC<AssetBriefProps> = ({
  unit,
  refreshOverviewData,
  planDetails = [],
  regeneratePlanTrigger,
  hideGenerateButton = false,
}) => {
  const { LLM_SERVICE } = useRuntimeVariables()
  const { theme } = useGeckoTheme()
  const { authorizedAxios: axios } = useGeckoAuth()
  const siteID = unit.tags.site
  const unitID = unit.id

  const { reports, isLoading, hasEverLoaded, refreshData } = useSiteFilesData(
    siteID,
    unitID
  )
  const { rolodexPut } = useRolodexEdit()
  const { data: components } = useGetRolodexEntriesByLoadOptions<
    {
      entry: { id: string; data: { name: string } }
    }[]
  >({
    type: [RolodexEntryType.COMPONENT],
    tags: { unit: unitID },
  })

  const componentIDs: string[] = components
    ? components.map((component) => component.entry.id)
    : []
  const { data: minRLData, isLoading: minRLIsLoading } =
    useGetBulkComponentMinRls(componentIDs)

  const componentNameToMinRL: ComponentNameAndMinRL[] =
    minRLData && !minRLIsLoading
      ? componentIDs.reduce<ComponentNameAndMinRL[]>((result, componentID) => {
          const component = components
            ? components.find((component) => component.entry.id === componentID)
            : undefined
          const minRL =
            componentID in minRLData ? minRLData[componentID] : undefined
          if (component && minRL && !isErrorInfo(minRL)) {
            result.push({
              componentName: component.entry.data.name,
              remainingLife: minRL.rl.minRl,
            })
          }
          return result
        }, [])
      : []

  const unitLowestRemainingLife = unit?.data.current_brief
    ?.lowestMinimumRemainingLife
    ? getComponentHealthCondition(
        unit.data.current_brief.lowestMinimumRemainingLife
      )
    : undefined

  const remainingLifeColor = unitLowestRemainingLife
    ? HealthConditionColors[unitLowestRemainingLife]
    : undefined

  const [claudeLoading, setClaudeLoading] = useState<LoadingStage>(
    LoadingStage.NotLoading
  )
  const [claudeResponse, setClaudeResponse] = useState<
    Anthropic.Message | undefined
  >(undefined)
  const [claudePlanResponse, setClaudePlanResponse] = useState<
    Anthropic.Message | undefined
  >(undefined)
  const [modalVisible, setModalVisible] = useState<boolean>(false)
  const [additionalComments, setAdditionalComments] = useState<string>('')
  const [fileArrays, setFileArrays] = useState<FileDetails[]>([])
  const [isGeneratingPlan, setIsGeneratingPlan] = useState<boolean>(false)

  // Track previous trigger to prevent unnecessary regeneration
  const previousTriggerRef = useRef<number | undefined>(undefined)

  const componentList = componentIDs.map((id) => ({ componentId: id }))
  const { data: bulkDamageAreas, isLoading: bulkDamageLoading } =
    useGetBulkDamageAreas(componentList)

  // Move to helper file
  const extractClaudeText = useCallback(
    (claudeResponse: Anthropic.Message): string => {
      const content: Anthropic.ContentBlock[] = claudeResponse.content
      if (content.length >= 1) {
        const firstContent = content[0] as Anthropic.TextBlock
        return firstContent.text
      } else {
        return 'There was an error extracting the content from the model response!'
      }
    },
    []
  )

  // helper function for generating plan summary
  const generatePlanSummaryContent = useCallback(
    async (
      planDetails: Anthropic.TextBlockParam[]
    ): Promise<Anthropic.Message | undefined> => {
      if (planDetails.length === 0) return undefined

      const planContent = []
      const planInstructions: Anthropic.TextBlockParam = {
        type: 'text',
        text: `Use the provided plan information to summarize what our health plan for the asset is in 1-2 sentences. In particular, call out the nearest-term action, its timing, and cost.`,
      }
      planContent.push(planInstructions)
      planContent.push(...planDetails)

      const planMessageContent: Anthropic.MessageParam[] = [
        {
          role: 'user',
          content: planContent,
        },
      ]

      try {
        const planResponse = await askClaude(
          planMessageContent,
          axios,
          LLM_SERVICE
        )
        return planResponse
      } catch (error) {
        console.error('Error generating plan summary:', error)
        return undefined
      }
    },
    [axios, LLM_SERVICE]
  )

  const generatePlanSummary = useCallback(async () => {
    if (!unit || planDetails.length === 0) return

    setIsGeneratingPlan(true)

    try {
      const planResponse = await generatePlanSummaryContent(planDetails)

      if (planResponse) {
        const planSummary = extractClaudeText(planResponse)

        // Update only the plan summary, preserving the rest of current_brief
        const editedUnit: RolodexEntryPutJson = unit.raw as RolodexEntryPutJson
        editedUnit.data.current_brief = {
          ...unit.data.current_brief,
          planSummary: planSummary,
        }

        await rolodexPut([editedUnit])
        await refreshOverviewData()
      }
    } catch (error) {
      console.error('Error saving plan summary:', error)
    } finally {
      setIsGeneratingPlan(false)
    }
  }, [
    unit,
    planDetails,
    rolodexPut,
    refreshOverviewData,
    extractClaudeText,
    generatePlanSummaryContent,
  ])

  // Trigger plan generation when the plan trigger changes and is defined
  // ensure that we have a condition summary and plan information before regenerating
  useEffect(() => {
    if (
      regeneratePlanTrigger !== undefined &&
      regeneratePlanTrigger !== previousTriggerRef.current &&
      planDetails.length > 0 &&
      unit?.data.current_brief?.conditionSummary
    ) {
      previousTriggerRef.current = regeneratePlanTrigger
      generatePlanSummary()
    }
  }, [
    regeneratePlanTrigger,
    planDetails.length,
    unit?.data.current_brief?.conditionSummary,
    generatePlanSummary,
  ])

  const onGenerateSummaryClick = async () => {
    setClaudeLoading(LoadingStage.BuildingContent)
    // Move to separate file
    const fileBlobs: Blob[] = await Promise.all(
      fileArrays.map(async (file: FileDetails) => {
        const response = await fetch(file.signed_url)
        // Move out. Each file_type should have a function to call
        if (file.file_type === 'docx') {
          const arrayBuffer = await response.arrayBuffer()
          return await convertDOCXtoPDFBlob(arrayBuffer)
        }
        // Throw error instead of assuming it's a PDF
        return await response.blob()
      })
    )

    // Move to its own file
    const fileBase64Strings: string[] = await Promise.all(
      fileBlobs.map(
        (blob) =>
          new Promise<string>((resolve, reject) => {
            const reader = new FileReader()
            reader.onloadend = () => {
              if (reader.result) {
                const result: string = reader.result as string
                resolve(result.split(',')[1])
              } else {
                resolve('')
              }
            }
            reader.onerror = reject
            reader.readAsDataURL(blob)
          })
      )
    )

    // Move to a separate file. We should have a "generate content block" function for each supported type
    const fileContent: Anthropic.DocumentBlockParam[] = fileBase64Strings.map(
      (fileB64) => {
        return {
          type: 'document',
          source: {
            data: fileB64,
            media_type: 'application/pdf',
            type: 'base64',
          },
        }
      }
    )

    const remainingLifeContent: Anthropic.TextBlockParam = {
      type: 'text',
      text: `The following is the calculated remaining life in years for the components of this asset. Use it as a part of the analysis: ${JSON.stringify(componentNameToMinRL)}`,
    }

    // Create damage areas content
    let damageAreasContent: Anthropic.TextBlockParam | null = null
    if (bulkDamageAreas && Object.keys(bulkDamageAreas).length > 0) {
      const damageAreasText = Object.entries(bulkDamageAreas)
        .map(([componentId, damageData]) => {
          const component = components?.find((c) => c.entry.id === componentId)
          if (!component || !(DEFAULT_DAMAGE_AREAS_PLAN_KEY in damageData))
            return null

          const baseData = isErrorInfo(
            damageData[DEFAULT_DAMAGE_AREAS_PLAN_KEY]
          )
            ? undefined
            : damageData[DEFAULT_DAMAGE_AREAS_PLAN_KEY][
                dayjs().year().toString()
              ]

          // return the top 5 damage areas based on probability * consequence
          const topDamageAreas = baseData?.damageAreas
            ? baseData.damageAreas
                .sort(
                  (a, b) =>
                    (b.probability ?? 0) * (b.consequence ?? 0) -
                    (a.probability ?? 0) * (a.consequence ?? 0)
                )
                .slice(0, 5)
            : []

          return topDamageAreas.length > 0
            ? {
                component: component.entry.data.name || 'Unknown',
                damageAreas: topDamageAreas,
              }
            : null
        })
        .filter(Boolean)

      if (damageAreasText.length > 0) {
        damageAreasContent = {
          type: 'text',
          text: `The following are the current most significant damage areas on the asset, with the [number, number] format of x and y being start and end in mm: ${JSON.stringify(damageAreasText)}`,
        }
      }
    }

    // Move to "generate content block" file
    const textContent: Anthropic.TextBlockParam = {
      type: 'text',
      text: additionalComments,
    }

    // Create condition content
    const conditionContent = []
    const conditionInstructions: Anthropic.TextBlockParam = {
      type: 'text',
      text: `Generate a brief asset condition summary in 1-2 sentences. Focus on the overall health status, minimum remaining life, and any critical damage areas that drive the condition assessment.`,
    }
    conditionContent.push(conditionInstructions)
    if (additionalComments) conditionContent.push(textContent)
    if (fileContent.length > 0) conditionContent.push(...fileContent)
    if (componentNameToMinRL.length > 0)
      conditionContent.push(remainingLifeContent)
    if (damageAreasContent) conditionContent.push(damageAreasContent)

    const conditionMessageContent: Anthropic.MessageParam[] = [
      {
        role: 'user',
        content: conditionContent,
      },
    ]

    setClaudeLoading(LoadingStage.Summarizing)

    // Always make condition call
    const conditionResponse = await askClaude(
      conditionMessageContent,
      axios,
      LLM_SERVICE
    )
    setClaudeResponse(conditionResponse)

    if (planDetails.length > 0) {
      const planResponse = await generatePlanSummaryContent(planDetails)
      setClaudePlanResponse(planResponse)
    } else {
      setClaudePlanResponse(undefined)
    }

    setClaudeLoading(LoadingStage.NotLoading)
  }

  const onCheckboxClick = (clickedFile: FileDetails) => {
    if (fileArrays.find((file) => file.entry_ID === clickedFile.entry_ID)) {
      setFileArrays(
        fileArrays.filter((file) => file.entry_ID !== clickedFile.entry_ID)
      )
    } else {
      setFileArrays([...fileArrays, clickedFile])
    }
  }

  // Function should accept a string as a parameter, rather than relying on state
  const saveBriefToUnit = () => {
    setClaudeLoading(LoadingStage.Saving)
    if (!claudeResponse) return
    if (!unit) return

    const conditionSummary: string = extractClaudeText(claudeResponse)
    const planSummary: string | undefined = claudePlanResponse
      ? extractClaudeText(claudePlanResponse)
      : undefined
    const remainingLifeList = componentNameToMinRL.map(
      (component) => component.remainingLife
    )
    const lowestMinimumRL: number | undefined =
      remainingLifeList.length > 0 ? Math.min(...remainingLifeList) : undefined

    const editedUnit: RolodexEntryPutJson = unit.raw as RolodexEntryPutJson

    // If we generated a new plan summary, use it; otherwise preserve the existing one
    const updatedPlanSummary =
      planSummary !== undefined
        ? planSummary
        : unit.data.current_brief?.planSummary

    editedUnit.data.current_brief = {
      conditionSummary: conditionSummary,
      planSummary: updatedPlanSummary,
      lowestMinimumRemainingLife: lowestMinimumRL,
    }
    rolodexPut([editedUnit])
      .then(() => {
        return refreshOverviewData()
      })
      .then(() => {
        setModalVisible(false)
        setClaudeResponse(undefined)
        setClaudePlanResponse(undefined)
      })
      .finally(() => {
        setClaudeLoading(LoadingStage.NotLoading)
      })
  }

  // Move out of React component
  const AssetBriefFilesColumns: ColumnDef<FilesTableEntry>[] = [
    {
      accessorKey: 'isChecked',
      id: 'isChecked',
      header: '',
      cell: ({ row }) => {
        return (
          <CheckboxCell
            row={row.original}
            onCheckboxClick={onCheckboxClick}
            checked={Boolean(
              fileArrays.find((file) => {
                return row.original.id === file.entry_ID
              })
            )}
          />
        )
      },
    },
    {
      accessorKey: 'visibleName',
      id: 'visibleName',
      header: 'File Name',
      filterFn: 'includesString',
      sortingFn: 'alphanumeric',
      sortUndefined: 'last',
      meta: {
        inputType: InputType.Text,
      },
      cell: ({ row }) =>
        row.original.visibleName.length > 50 ? (
          <TextCell truncate text={row.original.visibleName} />
        ) : (
          <TextCell text={row.original.visibleName} />
        ),
    },
    {
      accessorKey: 'uploadDate',
      id: 'uploadDate',
      header: 'Upload Date',
      sortingFn: 'datetime',
      sortUndefined: 'last',
      filterFn: 'dateRangeFilter',
      meta: {
        inputType: InputType.Date,
      },
      cell: ({ row }) => (
        <TextCell text={parseISO(row.original.uploadDate)?.toDateString()} />
      ),
    },
    {
      accessorKey: 'componentNames',
      id: 'componentNames',
      header: 'Component Tag(s)',
      sortUndefined: 'last',
      sortingFn: 'alphanumeric',
      filterFn: 'multiSelectFilter',
      meta: {
        inputType: InputType.MultiSelect,
      },
      cell: ({ row }) =>
        row.original.componentNames.length > 0 ? (
          row.original.componentNames.map((componentName) => (
            <TagCell
              key={componentName}
              text={componentName}
              color={theme.forecast.defaultButton}
            />
          ))
        ) : (
          <TextCell />
        ),
    },
  ]

  const allowedFiles = reports.filter(
    (file) => file.data.fileType === 'docx' || file.data.fileType === 'pdf'
  )

  // Move Modal out into its own component
  return (
    <CantileverCard>
      <CantileverCard.CardHeader
        leadingContent='Asset Brief'
        trailingContent={<span>Powered by AIR&trade;</span>}
        style={{ backgroundColor: `${remainingLifeColor}` }}
      />
      <CantileverCard.Section
        style={{ backgroundColor: `${remainingLifeColor}` }}>
        <ContentContainer>
          {unit?.data.current_brief?.conditionSummary ||
          unit?.data.current_brief?.planSummary ? (
            <SummarySection>
              {unit.data.current_brief.conditionSummary && (
                <div>
                  <SectionTitle>Condition</SectionTitle>
                  <SectionText>
                    {unit.data.current_brief.conditionSummary}
                  </SectionText>
                </div>
              )}
              {unit.data.current_brief.planSummary && (
                <div>
                  <SectionTitle>Plan</SectionTitle>
                  <SectionText>
                    {unit.data.current_brief.planSummary}
                  </SectionText>
                </div>
              )}
              {isGeneratingPlan && (
                <div>
                  <SectionTitle>Plan</SectionTitle>
                  <Spin size='small' />
                </div>
              )}
            </SummarySection>
          ) : (
            <NoBriefContainer>
              No Content has been generated by AIR for this Asset
            </NoBriefContainer>
          )}
          {!hideGenerateButton && (
            <ButtonContainer>
              <PermissionsGate
                requires={[PERMISSION_CANTILEVER_ASSET_OVERVIEW_EDITOR]}>
                <CantileverButton onClick={() => setModalVisible(true)}>
                  Generate Asset Brief
                </CantileverButton>
              </PermissionsGate>
            </ButtonContainer>
          )}
        </ContentContainer>
        <Modal
          open={modalVisible}
          title={'Generate Asset Brief'}
          onCancel={() => setModalVisible(false)}
          footer={null}
          width={'50%'}>
          <Spin
            tip={
              claudeLoading === LoadingStage.BuildingContent
                ? 'Gathering Data'
                : claudeLoading === LoadingStage.Summarizing
                  ? 'Summarizing Content'
                  : 'Saving'
            }
            spinning={claudeLoading !== LoadingStage.NotLoading}>
            <ModalContentContainer>
              <div>
                Generate a summary of this asset using AIR&trade;. Please select
                what inputs you&apos;d like to provide our model below
              </div>
              <AssetFilesTable
                unitID={unitID}
                reports={allowedFiles}
                isLoading={isLoading}
                hasEverLoaded={hasEverLoaded}
                forceRefresh={refreshData}
                customColumns={AssetBriefFilesColumns}
                showColumnConfig={false}
                showFilterBar={false}
              />
              <div>
                <Spin spinning={minRLIsLoading || bulkDamageLoading}>
                  <span>
                    Remaining Life:{' '}
                    {componentNameToMinRL.length === 0
                      ? 'No available remaining life values to use on this asset'
                      : `Remaining life values will be included for: ${componentNameToMinRL.map((component) => component.componentName).join(', ')}`}
                  </span>
                </Spin>
              </div>
              <div>
                <span>Additional Comments:</span>
                <TextInput
                  $borderColor={'grey'}
                  onChange={(e) => setAdditionalComments(e.target.value)}
                />
              </div>
              <ButtonContainer>
                <CantileverButton
                  onClick={onGenerateSummaryClick}
                  disabled={
                    fileArrays.length === 0 ||
                    minRLIsLoading ||
                    bulkDamageLoading
                  }>
                  Generate Summary
                </CantileverButton>
              </ButtonContainer>
              <hr />
              <div>
                {claudeResponse || claudePlanResponse ? (
                  <SummarySection>
                    {claudeResponse && (
                      <div>
                        <SectionTitle>Condition</SectionTitle>
                        <SectionText>
                          {extractClaudeText(claudeResponse)}
                        </SectionText>
                      </div>
                    )}
                    {claudePlanResponse && (
                      <div>
                        <SectionTitle>Plan</SectionTitle>
                        <SectionText>
                          {extractClaudeText(claudePlanResponse)}
                        </SectionText>
                      </div>
                    )}
                  </SummarySection>
                ) : (
                  <NoBriefContainer>
                    No Content has been generated by AIR for this Asset
                  </NoBriefContainer>
                )}
              </div>
              <ButtonContainer>
                <CantileverButton
                  onClick={saveBriefToUnit}
                  disabled={!claudeResponse}>
                  Save Summary to Asset
                </CantileverButton>
              </ButtonContainer>
            </ModalContentContainer>
          </Spin>
        </Modal>
      </CantileverCard.Section>
    </CantileverCard>
  )
}
